import { CommonModule } from '@angular/common';
import { Component, input, OnInit, signal, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AwBadgeComponent,
  AwBreadCrumbComponent,
  AwButtonDirective,
  AwDialogComponent,
  AwDividerComponent,
  AwFormFieldLabelComponent,
  AwSearchComponent,
  AwSelectMenuComponent,
  AwSpinnerComponent,
  AwTableComponent,
  AwToastComponent,
  BreadCrumb,
  DialogFormControls,
  DialogOptions,
  SelectedOption,
  TableCellInput,
  TableActionMenu,
  TableCellTypes,
  TableRow,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import { ParentIntegrationService } from '../../../../../core/services/parent-integration.service';
import { IntegrationService } from '../../../../../core/services/integration.service';
import { Integration, ParentIntegration } from '../../../../../api/models';
import { catchError, finalize, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';
import { UserService, Organization } from '../../../../../core/services/user.service';
import { MatIcon } from '@angular/material/icon';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Update_3$Params } from '../../../../../api/fn/parent-integrations/update-3';
import { linkCellTemplate } from '../../../../../shared/utils/link-cell-template';
import { TableActionBarComponent } from '../../../../../shared/components/table-action-bar/table-action-bar.component';
import {
  ASSOCIATED_FILTER_OPTIONS,
  DEFAULT_ASSOCIATED_FILTER,
  filterByAssociation,
} from '../../../../../shared/utils/table-filters';

import {
  associationBadge,
  associationRowActions,
  IntegrationRow,
  statusBadge,
} from '../../../../../shared/utils/association-table';
@Component({
    selector: 'app-parent-integration-details',
    imports: [
        CommonModule,
        AwBreadCrumbComponent,
        AwButtonDirective,
        AwDividerComponent,
        AwSpinnerComponent,
        AwToastComponent,
        AwSearchComponent,
        AwTableComponent,
        AwDialogComponent,
        AwSelectMenuComponent,
        AwFormFieldLabelComponent,
        MatIcon,
        FormsModule,
        ReactiveFormsModule,
        TableActionBarComponent,
    ],
    templateUrl: './parent-integration-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './parent-integration-details.component.scss',
        '../../../../../../styles/management.scss',
    ]
})
export class ParentIntegrationDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly parentIntegrationService = inject(ParentIntegrationService);
  private readonly integrationService = inject(IntegrationService);
  private readonly userService = inject(UserService);

  constructor() {
    // CCL's multi-select re-emits the same array it mutates, so filter on a copy.
    this.associatedFilter.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.applyAssociatedFilter());
  }

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;
  @ViewChild(AwTableComponent) awTableComponent!: AwTableComponent;

  isLoading: boolean = false;
  parentIntegrationInfo!: ParentIntegration | undefined;
  parentIntegrationInfoDividers = signal<any[]>([]);
  parentIntegrationDateDividers = signal<any[]>([]);

  userPermissions: Record<string, boolean> = {};
  organization: Organization | null = null;

  integrationsTableData = signal<IntegrationRow[]>([]);
  integrationsTableDataCopy: IntegrationRow[] = [];
  integrationSearchValue: string = '';

  openDisassociateIntegrationDialogSingle: boolean = false;
  openChangeIntegrationStatus: boolean = false;
  openAssociateIntegrationDialog: boolean = false;

  checkedRows: TableRow[] = [];
  /** The rows the Associate dialog will add: checked rows from the bar, or one row from its menu. */
  rowsToAssociate: TableRow[] = [];
  singleIntegrationToDisassociate: TableRow | null = null;

  disableIntegrationChangeStatus: boolean = true;
  disableIntegrationAssociate: boolean = true;

  readonly associatedFilterOptions = ASSOCIATED_FILTER_OPTIONS;
  associatedFilter = new FormControl<SelectedOption[]>([...DEFAULT_ASSOCIATED_FILTER]);

  breadcrumbs = signal<BreadCrumb[]>([
    {
      label: 'Third-Party Management',
      route: '/parent-integration-management/overview',
      newTab: false,
    },
  ]);

  dialogOptionsDisassociateSingle: DialogOptions = {
    variant: 'alert',
    title: 'Change Association',
    description: 'By selecting Continue, you will disassociate the selected Third-Party Integration from this Third Party. Choose Cancel to go back.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Continue',
  };

  dialogOptionsChangeStatus: DialogOptions = {
    variant: 'standard',
    title: 'Change Third-Party Integration Status',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Change',
  };

  dialogOptionsAssociate: DialogOptions = {
    variant: 'alert',
    title: 'Associate Third-Party Integrations',
    description: 'By selecting Continue, you will associate the selected Third-Party Integration(s) with this Third Party. Choose Cancel to go back.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Continue',
  };

  changeStatusFormFields = input<DialogFormControls>({
    controls: [
      {
        formControlName: 'changeStatus',
        label: 'Change Status',
        controlRequired: true,
        value: '',
        type: 'select',
        selectOptions: {
          enableSearch: false,
          singleSelectOptions: [
            { label: 'Active', value: true },
            { label: 'Disabled', value: false },
          ],
        },
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      },
    ],
  });

  integrationsColumnsDefinition: TableCellInput[] = [
    {
      align: 'left',
      type: TableCellTypes.Custom,
      isExpanded: false,
      // CCL sorts a combined column by its key (table.mdx, 'custom columns that use combineFields'), so the key is the
      // displayed name; an object or missing field left sorting dead.
      key: 'integrationInfo.integrationName',
      label: 'Third-Party Integration Name/ID',
      combineFields: [
        'integrationInfo.integrationName',
        'integrationInfo.detailsPageRoute',
        'integrationInfo.integrationId',
      ],
      combineTemplate: (values: any[]) => ({
        template: linkCellTemplate(values[0], values[1], values[2]),
      }),
      sort: true,
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Associated',
      label: 'Associated',
      combineFields: ['associationBadge.color', 'associationBadge.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Status',
      combineFields: ['badgeNested.color', 'badgeNested.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
  ];

  headerButtonActions = signal<any[]>([
    {
      iconButtonCallback: {
        icon: 'edit',
        label: 'Edit third party',
        action: () => {
          this.router.navigate(['edit'], { relativeTo: this.route });
        },
      },
    },
  ]);

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  ngOnInit(): void {
    this.userPermissions = this.userService.getUserPermissions();
    this.organization = this.userService.getOrganization();

    if (this.organization === 'integration-employee') {
      const associatedColumnIndex = this.integrationsColumnsDefinition.findIndex(
        col => col.key === 'Associated'
      );
      if (associatedColumnIndex !== -1) {
        this.integrationsColumnsDefinition.splice(associatedColumnIndex, 1);
      }
    }

    if (this.organization === 'assetworks-employee' && (this.userPermissions['canDelete'] || this.userPermissions['canEdit'])) {
      this.integrationsColumnsDefinition.unshift({
        type: TableCellTypes.Checkbox,
        key: 'checkbox',
        isExpanded: false,
        align: 'center',
      });
    }

    this.buildAllData();
  }

  buildAllData(): void {
    this.isLoading = true;

    this.route.paramMap
      .pipe(
        map((params) => {
          const parentIntegrationId = params.get('parentIntegrationId')!;
          this.breadcrumbs.set([
            ...this.breadcrumbs(),
            {
              label: 'Third-Party Details',
              route: `/parent-integration-management/parent-integration-details/${parentIntegrationId}`,
              newTab: false,
            },
          ]);
          return parentIntegrationId;
        }),
        take(1),
        switchMap((parentIntegrationId) =>
          this.parentIntegrationService.getParentIntegrationById({
            organizationId: parentIntegrationId,
          })
        ),
        switchMap((parentIntegration) => {
          if (!parentIntegration) {
            return of([]);
          }

          this.parentIntegrationInfo = parentIntegration;

          this.parentIntegrationInfoDividers.set([
            {
              title: 'Third-Party Name',
              info: parentIntegration.name ?? 'Unable to Retrieve',
            },
            {
              title: 'Organization ID',
              info: parentIntegration.organizationId ?? 'Unable to Retrieve',
            },
            {
              title: 'Status',
              info: parentIntegration.enabled ? 'Active' : 'Disabled',
            },
            {
              title: 'Third-Party Employee Authentication',
              info: 'OpenID Connect',
            },
          ]);

          this.parentIntegrationDateDividers.set([
            {
              title: 'Created Date',
              info: parentIntegration.createdOn
                ? new Date(parentIntegration.createdOn).toLocaleDateString()
                : 'N/A',
            },
            {
              title: 'Last Modified Date',
              info: parentIntegration.modifiedOn
                ? new Date(parentIntegration.modifiedOn).toLocaleDateString()
                : 'N/A',
            },
          ]);

          // For integration employees, fetch only associated integrations
          // For AssetWorks employees, fetch all integrations
          if (this.organization === 'integration-employee') {
            const integrationIds = parentIntegration.integrationIds || [];
            if (integrationIds.length === 0) {
              return of([]);
            }
            return forkJoin(
              integrationIds.map(id =>
                this.integrationService.getIntegrationById({ organizationId: id })
                  .pipe(catchError(() => of(null)))
              )
            ).pipe(
              map(integrations => integrations.filter((i): i is any => i !== null)),
              catchError(() => {
                this.buildToastMessage('Failed to load third-party integrations', 'error');
                return of([]);
              })
            );
          } else {
            return this.integrationService.getAlIntegrations().pipe(
              catchError(() => {
                this.buildToastMessage('Failed to load third-party integrations', 'error');
                return of([]);
              }),
              map((integrations) => integrations || [])
            );
          }
        }),
        catchError(() => {
          this.buildToastMessage('Failed to load third-party data', 'error');
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((integrations) => {
        this.integrationsTableDataCopy = integrations.map((integration) => this.toIntegrationRow(integration));
        this.applyAssociatedFilter();
      });
  }

  /** Shows the rows matching the Associated filter. The table keeps its search text across data changes. */
  applyAssociatedFilter(): void {
    this.integrationsTableData.set(
      filterByAssociation(this.integrationsTableDataCopy, [...(this.associatedFilter.value ?? [])])
    );
  }

  private toIntegrationRow(integration: Integration | null | undefined): IntegrationRow {
    const isAssociated =
      this.parentIntegrationInfo?.integrationIds?.includes(integration?.organizationId ?? '') ?? false;

    return {
      integrationInfo: {
        integrationName: integration?.organizationName ?? '',
        integrationId: integration?.organizationId ?? '',
        detailsPageRoute: `/integration-management/integration-details/${integration?.organizationId}`,
      },
      associationBadge: associationBadge(isAssociated),
      badgeNested: statusBadge(integration?.enabled ?? false),
      authType: integration?.authenticationType,
      customerIds: integration?.customerIds || [],
    };
  }

  checkBoxActionHandler(checkedRows: { rows: TableRow[]; key: string }): void {
    this.checkedRows = checkedRows.rows;
    const nonAssociatedRows = this.checkedRows.filter(
      (row) => !row['associationBadge']?.isAssociated
    );

    // Status belongs to the integration, not the association, so any checked row can change it (as in the row menu).
    this.disableIntegrationChangeStatus = this.checkedRows.length === 0;
    this.disableIntegrationAssociate = nonAssociatedRows.length === 0;
  }

  onSearchTable(searchInput: string): void {
    this.awTableComponent.applyFilter(searchInput);
  }

  dialogFormCloseHandler(key: string): void {
    if (key === 'disassociate') {
      this.openDisassociateIntegrationDialogSingle = false;
    } else if (key === 'change-status') {
      this.openChangeIntegrationStatus = false;
    } else if (key === 'associate') {
      this.openAssociateIntegrationDialog = false;
    }
  }

  disassociateIntegrationFormSubmit(): void {
    if (!this.singleIntegrationToDisassociate) {
      this.buildToastMessage('No third-party integration selected', 'error');
      return;
    }

    this.isLoading = true;

    const removedIntegrationId =
      this.singleIntegrationToDisassociate['integrationInfo'].integrationId;
    const newIntegrationIds = (
      this.parentIntegrationInfo?.integrationIds ?? []
    ).filter((id) => id !== removedIntegrationId);

    const params: Update_3$Params = {
      organizationId: this.parentIntegrationInfo?.organizationId || '',
      body: {
        name: this.parentIntegrationInfo?.name ?? '',
        enabled: this.parentIntegrationInfo?.enabled || false,
        integrationIds: newIntegrationIds,
      },
    };

    this.parentIntegrationService.updateParentIntegration(params).subscribe((success) => {
      if (success) {
        this.buildToastMessage('Successfully disassociated third-party integration', 'success');

        this.parentIntegrationInfo!.integrationIds = newIntegrationIds;

        const integrationToUpdate = this.integrationsTableDataCopy.find(
          (integration) =>
            integration['integrationInfo'].integrationId === removedIntegrationId
        );
        if (integrationToUpdate) {
          Object.assign(integrationToUpdate.associationBadge, associationBadge(false));
        }
        this.applyAssociatedFilter();
      } else {
        this.buildToastMessage('Failed to disassociate third-party integration', 'error');
      }
      this.openDisassociateIntegrationDialogSingle = false;
      this.isLoading = false;
    });
  }

  associateIntegrationsFormSubmit(): void {
    const nonAssociatedIntegrations = this.rowsToAssociate;

    if (!nonAssociatedIntegrations.length) {
      this.buildToastMessage('No non-associated third-party integrations selected', 'error');
      return;
    }

    this.isLoading = true;

    const integrationIdsToAdd = nonAssociatedIntegrations.map(
      (i) => i['integrationInfo'].integrationId
    );
    const newIntegrationIds = [
      ...(this.parentIntegrationInfo?.integrationIds ?? []),
      ...integrationIdsToAdd,
    ];

    const params: Update_3$Params = {
      organizationId: this.parentIntegrationInfo?.organizationId || '',
      body: {
        name: this.parentIntegrationInfo?.name ?? '',
        enabled: this.parentIntegrationInfo?.enabled || false,
        integrationIds: newIntegrationIds,
      },
    };

    this.parentIntegrationService
      .updateParentIntegration(params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openAssociateIntegrationDialog = false;
        })
      )
      .subscribe((success) => {
        if (success) {
          this.parentIntegrationInfo!.integrationIds = newIntegrationIds;

          nonAssociatedIntegrations.forEach((integration) => {
            Object.assign(integration['associationBadge'], associationBadge(true));
          });
          this.applyAssociatedFilter();

          this.buildToastMessage(
            `Successfully associated ${integrationIdsToAdd.length} third-party integration(s)`,
            'success'
          );
        } else {
          this.buildToastMessage('Failed to associate third-party integrations', 'error');
        }
      });
  }

  changeIntegrationStatusFormSubmit(formValues: any): void {
    if (!formValues.valid) {
      this.buildToastMessage('Please select a status', 'alert');
      return;
    }
    if (!this.checkedRows.length) {
      this.buildToastMessage('No rows selected', 'error');
      return;
    }

    const status: boolean = formValues.formData.changeStatus.value;

    const state = status ? 'Active' : 'Disabled';
    const integrationsToChange = this.checkedRows.filter(
      (r) => r['badgeNested'].enabled !== status
    );
    const skipped = this.checkedRows.length - integrationsToChange.length;

    if (!integrationsToChange.length) {
      this.buildToastMessage(`The selected third-party integrations are already ${state}`, 'alert');
      return;
    }

    this.isLoading = true;

    const statusChangeRequests: Observable<boolean>[] = integrationsToChange.map(
      (integration) => {
        const integrationInfo = integration['integrationInfo'];

        return this.integrationService
          .updateIntegration({
            organizationAuthType: integration['authType'],
            organizationName: integrationInfo.integrationName,
            organizationStatus: status,
            organizationPartnerIds: integration['customerIds'],
            organizationId: integrationInfo.integrationId,
          })
          .pipe(catchError(() => of(false)));
      }
    );

    forkJoin(statusChangeRequests)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openChangeIntegrationStatus = false;
        })
      )
      .subscribe((results) => {
        const numStatusUpdates = results.filter((updated) => updated).length;

        integrationsToChange.forEach((integration, index) => {
          if (results[index]) {
            Object.assign(integration['badgeNested'], statusBadge(status));
          }
        });
        this.applyAssociatedFilter();

        const skippedNote = skipped ? ` ${skipped} already ${state}.` : '';
        if (numStatusUpdates === integrationsToChange.length) {
          this.buildToastMessage(
            skipped
              ? `Set ${numStatusUpdates} of ${this.checkedRows.length} third-party integrations to ${state}.${skippedNote}`
              : `Set ${numStatusUpdates} third-party integrations to ${state}`,
            'success'
          );
        } else if (numStatusUpdates > 0) {
          this.buildToastMessage(
            `Set ${numStatusUpdates} of ${this.checkedRows.length} third-party integrations to ${state}. ${
              integrationsToChange.length - numStatusUpdates
            } failed.${skippedNote}`,
            'alert'
          );
        } else {
          this.buildToastMessage(`Failed to set the selected third-party integrations to ${state}`, 'error');
        }
      });
  }

  /**
   * The row's … menu (Kohl, 2026-10-16): Associate or Disassociate, and Set Active or Set Disabled, by the row's state.
   * Empty for anyone who can't act, which hides the row's menu (aw-table re-reads it per row).
   */
  rowActions = (row: TableRow): TableActionMenu[] => {
    const integration = row as IntegrationRow;
    return associationRowActions(integration, {
      canAssociate: this.userPermissions['canEdit'],
      canDisassociate: this.userPermissions['canDelete'],
      canSetStatus: this.userPermissions['canEdit'],
      associate: () => this.openAssociate([row]),
      disassociate: () => {
        this.singleIntegrationToDisassociate = row;
        this.openDisassociateIntegrationDialogSingle = true;
      },
      setStatus: (enabled) => this.setIntegrationStatus(integration, enabled),
    });
  };

  /** Associate, from the bar (checked rows) or a row's menu: confirms, then associateIntegrationsFormSubmit. */
  openAssociate(rows: TableRow[]): void {
    this.rowsToAssociate = rows.filter((row) => !row['associationBadge']?.isAssociated);
    this.openAssociateIntegrationDialog = true;
  }

  /** A row's Set Active / Set Disabled: applies at once and shows a toast. */
  setIntegrationStatus(row: IntegrationRow, enabled: boolean): void {
    const integrationInfo = row.integrationInfo;
    this.isLoading = true;
    this.integrationService
      .updateIntegration({
        organizationAuthType: row.authType,
        organizationName: integrationInfo.integrationName,
        organizationStatus: enabled,
        organizationPartnerIds: row.customerIds,
        organizationId: integrationInfo.integrationId,
      })
      // IntegrationService already maps a failed request to false.
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((updated) => {
        const state = enabled ? 'Active' : 'Disabled';
        if (!updated) {
          this.buildToastMessage(`Failed to set ${integrationInfo.integrationName} to ${state}`, 'error');
          return;
        }
        Object.assign(row.badgeNested, statusBadge(enabled));
        this.applyAssociatedFilter();
        this.buildToastMessage(`${integrationInfo.integrationName} set to ${state}`, 'success');
      });
  }
}
